import { useState } from "react";

import { setCookie } from "cookies-next";
import { POSTCODE_COOKIE_NAME } from "lib/constants";
import { DeliveryOrCollectionFormSchema } from "lib/schemas/schemas";
import bookingSlotResolver, { ResolvedBookingSlot } from "resolvers/data/pdp/bookingSlotResolver";
import type { ResolvedSite } from "resolvers/data/siteResolver";
import {
    NearestAvailability,
    getEarliestBookingSlots,
} from "services/motorpoint/getEarliestBookingSlots";
import { VehicleService } from "services/vehicle/vehicle";
import getCookieValue from "utils/getCookieValue";
import { z } from "zod";

import type { ClosestStoresContextProps } from "./closestStoresProvider";

interface IMergedStoreData extends ResolvedSite {
    bookingSlot: ResolvedBookingSlot;
}

const sortByLocality = (a: IMergedStoreData, b: IMergedStoreData) => {
    if (a.siteName! < b.siteName!) {
        return -1;
    }
    if (a.siteName! > b.siteName!) {
        return 1;
    }
    return 0;
};

const mergeClosestStoresData = (
    closestStores: NearestAvailability[],
    closestStoresDetails: ResolvedSite[]
): IMergedStoreData[] =>
    closestStores
        .map((store: NearestAvailability) => {
            const bookingSlot = bookingSlotResolver({ ...store, siteId: store.SiteId });
            const storeDetails = closestStoresDetails.find(
                ({ siteId }) => siteId === bookingSlot.siteId
            );

            return { ...storeDetails, bookingSlot } as unknown as IMergedStoreData;
        })
        .sort(sortByLocality);

const useClosestStores = (
    defaultValue: NearestAvailability[],
    allStoresDetailsData: ResolvedSite[] = [],
    vehicle: VehicleService.Vehicle
): ClosestStoresContextProps => {
    const [closestStores, setClosestStores] = useState(
        mergeClosestStoresData(defaultValue, allStoresDetailsData)
    );
    const [error, setError] = useState("");
    const [lastPostcode, setLastPostcode] = useState<string>(
        getCookieValue(POSTCODE_COOKIE_NAME) ?? ""
    );
    const getClosestStores = async ({
        postCode,
    }: z.infer<typeof DeliveryOrCollectionFormSchema>) => {
        postCode = postCode?.trim()?.replace(/\s/g, "");
        if (!postCode) {
            setError("Could not get closest stores");
            throw new Error("Could not get closest stores");
        }

        setLastPostcode(postCode);

        const vehicleIsSold = !!vehicle?.IsSold;
        const vehicleIsOnSale = !!vehicle?.IsOnSale;

        if (vehicleIsSold || !vehicleIsOnSale) {
            setError("Could not get closest stores");
            throw new Error("Could not get closest stores");
        }

        const inTransit = vehicle.IsInTransit || vehicle.AdvertisedLocation === null;

        const data = await getEarliestBookingSlots({
            CurrentSiteId: vehicle.AdvertisedLocation?.Id ?? vehicle.CurrentLocation.Id,
            CurrentSiteName: vehicle.AdvertisedLocation?.Name ?? vehicle.CurrentLocation.Name ?? "",
            HasDisabledV5: vehicle.HasMotabilityV5,
            AllocatedSiteId: vehicle.AllocatedLocation.Id,
            AllocatedSiteName: vehicle.AllocatedLocation.Name ?? "",
            IntendedSiteId: vehicle.IntendedLocation.Id,
            IntendedSiteName: vehicle.IntendedLocation.Name ?? "",
            IsCommercialVehicle: vehicle.Taxonomy.IsCommercial,
            IsInTransit: inTransit,
            Postcode: postCode,
            VehicleId: vehicle.LegacyVehicleId,
        });

        if (!data) {
            setError("Could not get closest stores");
            throw new Error("Could not get closest stores");
        }

        const validData = data?.Nearest.filter((item) => item?.FirstSlot);

        if (validData?.length) {
            setClosestStores(mergeClosestStoresData(validData, allStoresDetailsData));
            setCookie(POSTCODE_COOKIE_NAME, postCode, {
                maxAge: 99999999,
            });
            setError("");
        }
    };

    const reset = () => {
        setClosestStores([]);
        setLastPostcode("");
    };

    return {
        closestStores,
        getClosestStores,
        lastPostcode,
        hasClosestStores: !!(closestStores && closestStores?.length),
        allStoresDetailsData,
        reset,
        error,
    };
};

export default useClosestStores;
